class MattressUpsellSlider extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.waitForStylesheetAndLoad();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;

      // Disconnect document listeners
      this.documentListeners.forEach(listener => {
        document.removeEventListener(listener.type, listener.handler);
      });
      this.documentListeners = [];

      // Disconnect element listeners
      this.elementListeners.forEach(listener => {
        listener.el.removeEventListener(listener.type, listener.handler);
      });
      this.elementListeners = [];
    }
  }
  
  cacheElements() {
    this.documentListeners = [];
    this.elementListeners = [];

    this.slides = this.querySelectorAll('.MatUpsellSlider__Slide');
  }

  init() {
    this.attachListeners();
    this.initSwiper();
    this.syncToCurrentVariant();
  }

  waitForStylesheetAndLoad() {
    if (document.readyState == 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => {
        this.init();
      });
    }
  }

  attachListeners() {
    this.slides.forEach(slide => {
      const quickViewBtn = slide.querySelector('.MatUpsellSlider__QuickView');
      const atcBtn = slide.querySelector('.MatUpsellSlider__ATCBtn');
      const variantsJSON = JSON.parse(
        slide.querySelector('[data-variant-info-json]').innerHTML
      );

      // Open info popup (save handler for cleaning up later)
      const popupContents = slide.querySelector('[data-info-popup-contents]');
      const openPopupHandler = this.getOpenInfoPopupHandler(variantsJSON, popupContents);
      this.elementListeners.push({
        type: 'click',
        el: quickViewBtn,
        handler: openPopupHandler,
      });
      quickViewBtn.addEventListener('click', openPopupHandler);

      // Add relevant size to cart (save handler for cleanup)
      const atcBtnHandler = this.getATCBtnHandler(atcBtn, variantsJSON);
      this.elementListeners.push({
        type: 'click',
        el: atcBtn,
        handler: atcBtnHandler,
      });
      atcBtn.addEventListener('click', atcBtnHandler);
    });

    // Update DOM when variant changes
    const syncWithVariant = this.syncToCurrentVariant.bind(this);
    this.documentListeners.push({
      type: 'state:option-changed',
      handler: syncWithVariant,
    });
    document.addEventListener('state:option-changed', syncWithVariant);
  }

  getOpenInfoPopupHandler(variantsJSON, popupContents) {
    return (e) => {
      const matchingVariant = this.getMatchingVariant(variantsJSON.variants);

      window.GlobalNPopup.loadWithContents('product-card-addon', popupContents.innerHTML);

      // Update savings flag first
      const popup = GlobalNPopup.popup;
      const savingsFlag = popup.querySelector('.AddonInfoContents__SavingsFlag');
      const higherPrice = NUtils.getVariantStrikePrice(matchingVariant);
      const lowerPrice = NUtils.getVariantPrice(matchingVariant)
      const savingsAmt = higherPrice - lowerPrice;
      savingsFlag.innerHTML = `SAVE ${NUtils.formatMoney(savingsAmt)}`;

      window.GlobalNPopup.open();
    }
  }

  getATCBtnHandler(atcBtn, variantsJSON) {
    return (e) => {
      const matchingVariant = this.getMatchingVariant(variantsJSON.variants);

      const items = [{
        id: matchingVariant.id,
        quantity: 1,
      }];

      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      // Disable the button
      atcBtn.disabled = true;
      atcBtn.classList.add('BlueActionBtn--added');

      // Add to cart
      window.NolahCartManager.addItems(items)
        .then(res => {
          // Finish loading bar
          document.dispatchEvent(new CustomEvent('theme:loading:end'));

          // Uncomment the below to reenable button after a few seconds
          // setTimeout(() => {
          //   atcBtn.disabled = false;
          //   atcBtn.classList.remove('BlueActionBtn--added');
          // }, 6000);
        });
    }
  }

  syncToCurrentVariant() {
    this.slides.forEach(slide => {
      const sizeLabel = slide.querySelector('.MatUpsellSlider__SizeLabel');
      const strikeLabel = slide.querySelector('.MatUpsellSlider__StrikeLabel');
      const priceLabel = slide.querySelector('.MatUpsellSlider__Price');
      const atcBtn = slide.querySelector('.MatUpsellSlider__ATCBtn');

      // Update sold out state
      const variantsJSON = JSON.parse(
        slide.querySelector('[data-variant-info-json]').innerHTML
      );
      const matchingVariant = this.getMatchingVariant(variantsJSON.variants);
      if (!matchingVariant) {
        // Early return if sold out
        slide.classList.add('MatUpsellSlider__Slide--soldOut');
        return;
      } else {
        slide.classList.remove('MatUpsellSlider__Slide--soldOut');
      }
      
      // Update size label
      const currentSize = window.NolahPDPState.getOptionByName('Size');
      sizeLabel.innerHTML = `${currentSize} Size`;

      // Update price labels
      strikeLabel.innerHTML = `${NUtils.getVariantStrikePrice(matchingVariant, true)}`;
      priceLabel.innerHTML = `${NUtils.getVariantPrice(matchingVariant, true)}`;

      // Reset button
      atcBtn.classList.remove('BlueActionBtn--added');
      atcBtn.disabled = false;
    });
  }

  getMatchingVariant(variantsList) {
    const currentSize = window.NolahPDPState.getOptionByName('Size');
    let matchingVariant = variantsList.find(v => {
      return (v.options.includes(currentSize));
    });

    // If nothing was found, try allowing 'King' to match 'Split King' and
    // 'Cal King' to match 'Split Cal King'
    if (!matchingVariant) {
      if (currentSize === 'King' || currentSize === 'Cal King') {
        const sizeToFind = 'Split ' + currentSize;
        matchingVariant = variantsList.find(v => {
          return (v.options.includes(sizeToFind));
        });
      }
    }

    return matchingVariant;
  }

  initSwiper() {
    this.swiper = new Swiper('.MatUpsellSlider.swiper-container', {
      // slidesPerView: 1.2,
      centeredSlides: true,
      threshold: 15,
      spaceBetween: 16,
      roundLengths: true,
      navigation: {
        nextEl: '.MatUpsellSlider__Nav .swiper-button-next',
        prevEl: '.MatUpsellSlider__Nav .swiper-button-prev',
      },
      pagination: {
        el: '.MatUpsellSlider__Pagination',
        type: 'bullets',
        clickable: true,
      },
      breakpoints: {
        426: {
          slidesPerView: 1.03,
          spaceBetween: 30,
          centeredSlides: false,
        },
        769: {
          slidesPerView: 2,
          spaceBetween: 30,
          centeredSlides: false,
        },
        1126: {
          slidesPerView: 3,
          spaceBetween: 30,
          centeredSlides: false,
        },
      }
    });
  }
}
customElements.define('mattress-upsell-slider', MattressUpsellSlider);